/**
 * FILE-OVERVIEW:
 * - 役割: アプリ全体のルーティングと画面コンテナを管理。
 * - 主な連携: components/*, hooks/useAuth.js
 * - 本番運用上の注意: ルーティング変更時はヘッダー切替と権限制御を同時確認する。
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import FullCalendarComponent from './components/Calendar';
import MemoPage from './components/Memo';
import S3FilesPage from './components/S3Files';
import AppHeader from './components/Common/AppHeader';
import { Box } from '@mui/material';
import './App.css';
import './index.css';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { logInfo, logError } from './services/appLogger';

// App は index.js から user/signOut を受け取る場合があるので、
// props を優先し、無ければ useAuthenticator から得るようにします。
function App(props) {
  const parentUser = props.user;
  const parentSignOut = props.signOut;
  // useAuthenticator は必ず同じ順序で呼ぶ（条件付き禁止）
  const authCtx = useAuthenticator((c) => [c.user, c.signOut]);
  const user = parentUser ?? authCtx.user ?? null;
  const signOut = parentSignOut ?? authCtx.signOut ?? (() => {});
  const [userGroups, setUserGroups] = React.useState([]);
  const [isDesk, setIsDesk] = React.useState(false);
  const [isReadOnly, setIsReadOnly] = React.useState(false);
  const [isRootDrawerOpen, setIsRootDrawerOpen] = React.useState(false);

  // 追加: auth セッション情報を取得して子コンポーネントに確実に渡す
  const [authSession, setAuthSession] = React.useState(null);
  React.useEffect(() => {
    if (!user) {
      setAuthSession(null);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const [session, current] = await Promise.all([
          fetchAuthSession().catch(() => null),
          getCurrentUser().catch(() => null),
        ]);
        if (!mounted) return;

        const idToken = session?.tokens?.idToken?.toString?.() || null;
        const accessToken = session?.tokens?.accessToken?.toString?.() || null;
        const groups = session?.tokens?.idToken?.payload?.['cognito:groups'] || [];

        setAuthSession({
          session,
          user: current,
          idToken,
          accessToken,
          groups,
        });

        await logInfo('auth_session_resolved', {
          username: current?.username || null,
          groups,
          hasIdToken: !!idToken,
        }, `${current?.username || '不明ユーザー'}がログインしました。`);
      } catch (e) {
        console.error('failed to resolve auth session', e);
        await logError('auth_session_resolve_failed', {
          reason: e?.message || 'unknown_error',
        });
      }
    })();
    return () => { mounted = false; };
  }, [user]);

  React.useEffect(() => {
    // v6のsession優先でグループ取得し、無ければ従来形をフォールバックで参照
    if (user) {
      try {
        const groups =
          authSession?.groups ||
          (user.signInUserSession && user.signInUserSession.idToken && user.signInUserSession.idToken.payload && user.signInUserSession.idToken.payload['cognito:groups']) ||
          [];
        setUserGroups(groups);
        setIsDesk(groups.includes('Desks'));
        setIsReadOnly(groups.some((g) => String(g || '').toLowerCase() === 'read_only'));
      } catch (error) {
        console.error('ユーザーグループの取得に失敗:', error);
        setUserGroups([]);
        setIsDesk(false);
        setIsReadOnly(false);
      }
    } else {
      setUserGroups([]);
      setIsDesk(false);
      setIsReadOnly(false);
    }
  }, [user, authSession]);

  // 子に渡す user を合成：親から来た user を優先し、なければ useAuthenticator＋authSession 情報を併用
  const composedUser = React.useMemo(() => {
    const base = parentUser ?? user ?? null;
    if (!base) return null;
    // 既に signInUserSession があればそのまま使う
    if (base.signInUserSession) return base;
    // authSession に idToken があれば簡易セッションを埋める
    if (authSession && (authSession.idToken || (authSession.user && authSession.user.signInUserSession))) {
      const sessionFromAuth = authSession.user && authSession.user.signInUserSession ? authSession.user.signInUserSession
        : { idToken: { jwtToken: authSession.idToken }, accessToken: { jwtToken: authSession.accessToken } };
      return { ...base, signInUserSession: sessionFromAuth };
    }
    return base;
  }, [parentUser, user, authSession]);

  return (
    <Router>
      <Box
        className="App"
        translate="no"
        sx={{
          maxWidth: 1920,
          width: '100%',
          mx: 'auto',
          height: '100dvh',
          overflow: 'hidden',
        }}
      >
        <Routes>
          {/* ルート画面 */}
          <Route
            path="/"
            element={
              <Box
                className="schedule-root-layout"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: 0,
                  overflow: 'hidden',
                }}
              >
                <AppHeader
                  user={composedUser}
                  isDesk={isDesk}
                  onSignOut={signOut}
                  isSidebarOpen={isRootDrawerOpen}
                  onMenuToggle={setIsRootDrawerOpen}
                />
                <Box
                  className="schedule-main-area"
                  sx={{
                    maxWidth: 1920,
                    width: '100%',
                    mx: 'auto',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                  }}
                >
                  <FullCalendarComponent
                    user={composedUser}
                    isDesk={isDesk}
                    isReadOnly={isReadOnly}
                    onSignOut={signOut}
                    mobileDrawerOpen={isRootDrawerOpen}
                    setMobileDrawerOpen={setIsRootDrawerOpen}
                  />
                </Box>
              </Box>
            }
          />

          {/* メモ画面 */}
          <Route
            path="/memo"
            element={
              <MemoPage user={composedUser} isDesk={isDesk} isReadOnly={isReadOnly} onSignOut={signOut} />
            }
          />

          {/* S3ファイル一覧画面 */}
          <Route
            path="/files"
            element={
              <S3FilesPage user={composedUser} isDesk={isDesk} onSignOut={signOut} />
            }
          />
        </Routes>
      </Box>
    </Router>
  );
}

export default App;
